"use client";

import Image from "next/image";
import { useLanguage } from "../../context/LanguageContext";
import { formatMultilineText } from "../../lib/formatMultilineText";
import {
  InfoCard,
  infoCardIntroClassName,
  infoCardLabelClassName,
  infoCardLinkClassName,
  infoCardListClassName
} from "../InfoCard";
import { BulletItem } from "../BulletItem";

type HeroSectionProps = {
  onNavigate?: (section: "contact" | "home" | "approach" | "expertise" | "roles") => void;
};

export function HeroSection({ onNavigate }: HeroSectionProps): JSX.Element {
  const { t } = useLanguage();

  return (
    <section
      id="home"
      className="container-page scroll-mt-28 py-16 sm:py-20 lg:py-24"
      data-testid="hero-section"
    >
      <div id="hero" className="h-0 w-0" aria-hidden="true" />
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10 rtl:lg:flex-row-reverse">
        <div className="space-y-6 lg:flex-[1.4]" data-testid="hero-main-column">
          <div className="space-y-10">
            <h1
              className="text-3xl font-bold text-slate-900 dark:text-slate-100 sm:text-4xl lg:text-5xl"
              data-testid="hero-heading"
            >
              {t.hero.title}
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 sm:text-lg">
              {formatMultilineText(t.hero.subtitle)}
            </p>
            <p className="text-base text-slate-600 dark:text-slate-300">{formatMultilineText(t.hero.body)}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={() => onNavigate?.("roles")}
              className="w-full rounded-full border border-slate-200 dark:border-slate-700 px-5 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors hover:border-primary-200 hover:text-primary-700 dark:hover:border-primary-600 dark:hover:text-primary-400 sm:w-auto"
            >
              {t.common.buttons.viewRoles}
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.("expertise")}
              className="w-full rounded-full border border-slate-200 dark:border-slate-700 px-5 py-3 text-sm font-semibold text-slate-800 dark:text-slate-200 transition-colors hover:border-primary-200 hover:text-primary-700 dark:hover:border-primary-600 dark:hover:text-primary-400 sm:w-auto"
            >
              {t.common.nav.expertise}
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.("contact")}
              className="w-full rounded-full bg-primary-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 transition-colors hover:bg-primary-700 sm:w-auto text-center"
              data-testid="cta-primary"
            >
              {t.common.buttons.letsTalk}
            </button>
          </div>
          <div className="flex flex-col gap-4">
            {t.hero.cards.map((card, index) => (
              <InfoCard key={card.title}>
                <p className={infoCardLabelClassName}>{card.title}</p>
                <p className={infoCardIntroClassName}>{card.body}</p>
                {card.points && card.points.length > 0 && (
                  <ul className={infoCardListClassName}>
                    {card.points.map((point) => (
                      <BulletItem key={point} text={point} />
                    ))}
                  </ul>
                )}
                {index === 1 && (
                  <button
                    type="button"
                    onClick={() => onNavigate?.("roles")}
                    className={infoCardLinkClassName}
                  >
                    {card.rolesLinkLabel}
                  </button>
                )}
              </InfoCard>
            ))}
          </div>
        </div>
        <div
          className="flex flex-col gap-6 lg:flex-1 lg:flex-col lg:items-end lg:justify-end rtl:lg:items-start rtl:lg:justify-start"
          data-testid="hero-side-column"
        >
          <div className="mx-auto flex w-full max-w-[340px] flex-col-reverse gap-5 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-card transition-colors sm:max-w-[380px] lg:mx-0 lg:max-w-[460px] lg:flex-col lg:gap-5">
            <div className="space-y-5">
              <div className="deliver-card rounded-2xl p-6">
                <p className="text-sm font-semibold uppercase text-primary-700 dark:text-primary-300">
                  {t.hero.deliverTitle}
                </p>
                <ul className="mt-4 list-none space-y-3">
                  {t.hero.deliverList.map((item) => (
                    <BulletItem key={item} text={item} />
                  ))}
                </ul>
              </div>
              <InfoCard>
                <p className={infoCardLabelClassName}>{t.hero.howTitle}</p>
                <p className={infoCardIntroClassName}>{t.hero.howBody}</p>
                <ul className={infoCardListClassName}>
                  {t.hero.howList.map((item) => (
                    <BulletItem key={item} text={item} />
                  ))}
                </ul>
              </InfoCard>
            </div>
            <div className="group flex justify-center">
              <div className="hero-abstract-motion w-full">
                <Image
                  src="/hero-abstract.svg"
                  alt=""
                  aria-hidden="true"
                  width={1200}
                  height={900}
                  className="hero-abstract-img w-full object-contain opacity-90 dark:opacity-100"
                  priority
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
